import React, { useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { useTranslation } from 'react-i18next';
import { Box, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchSuppliers } from '../../slices/suppliers/suppliersSlice';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { SupplierWithId } from '../../models/supplier.model';
import styles from './SupplierProfiles.module.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../../common/message-modal/MessageModal';
import { useSupplierProfiles } from './hooks/useSupplierProfiles';
import { SuppliersTable } from './components/SuppliersTable';
import { SupplierRow } from './components/SupplierRow';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import { Helpers } from '../../common/helpers/helpers';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CategoryCard from '../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS } from '../../common/category-card/types';
import { ProfilesToolbar } from '../../common/profiles-list/ProfilesToolbar';
import listStyles from '../../common/profiles-list/ProfilesList.module.scss';

const SupplierProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [planMessageVisible, setPlanMessageVisible] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);

  const {
    suppliers: suppliersCreatedByAdmin,
    setCurrentPage,
    suppliersCount,
    currentPage,
    getRequestedPage,
    cardView,
    setCardView,
    suppliersLoading,
    resetSearchFilter,
    searchCleared,
    setSearchCleared,
    setSearchStringInputValue,
    searchStringInputValue,
  } = useSupplierProfiles();

  // Items per page - adjust this value according to your API
  const itemsPerPage = 20;
  // Calculate total pages
  const totalPages = Math.ceil(suppliersCount / itemsPerPage);
  // Shown in the actions block when there is more than one page.
  const showPagination = Boolean(suppliersCount && suppliersCount > itemsPerPage);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    getRequestedPage(page);
  };

  const company = useAppSelector(selectAllCompanies)[0];

  const [supplierId, setSupplierId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSupplierId(id);
  };
  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const handleProfileDelete = () => {
    if (supplierId) {
      setDeleteMessageModalOpen(false);
      NWClient.delete('supplier_profile', supplierId).then(() => {
        setSupplierId(null);
        dispatch(fetchSuppliers({ company_id: company?.id ?? 0, page: currentPage }));
        setAnchorEl(null);
      });
    }
  };

  const handleEdit = (id: number) => {
    navigate(ROUTES.SUPPLIER_PROFILES + '/' + id + '?initialTab=1');
  };

  const handleDocuments = (id: number) => {
    navigate(ROUTES.SUPPLIER_PROFILES + '/' + id + '?initialTab=0');
  };

  const handleDelete = (id: number) => {
    setSupplierId(id);
    setDeleteMessageModalOpen(true);
  };

  const handleCreateSupplier = () => {
    const planSettings = Helpers.getPlanSettings(company);
    if (!planSettings) return;

    const limit = planSettings.supplier_profile;
    const count = suppliersCreatedByAdmin?.length ?? 0;

    if (limit === 0) {
      setPlanMessageVisible(true);
    } else if (limit !== 'unlimited' && count >= Number(limit)) {
      setPlanMessageVisible(true);
    } else {
      navigate(ROUTES.SUPPLIER_PROFILE_CREATE);
    }
  };

  return (
    <>
      <AppHeader
        title={
          <>
            <span style={{ marginRight: '8px' }}>{t('supplierProfiles.header')}</span>
            <PreviewTooltip title={<Preview />} arrow placement='top'>
              <InfoIcon fontSize='small' className={'info-icon'} />
            </PreviewTooltip>
          </>
        }
      />
      <Grid component='div' container style={{ flexWrap: 'wrap' }}>
        <Box className={styles.page_description} style={{ width: '100%', marginTop: '16px' }}>
          {t('messages.supplierProfilesDescription')}
        </Box>
        <Grid component='div' size={{ xs: 12 }} className={styles.supplier_cards}>
          <Box
            id='supplierProfilesBlock'
            className={`${listStyles.profiles_block}${
              cardView ? '' : ` ${listStyles.profiles_block_joined}`
            }`}
          >
            <ProfilesToolbar
              idPrefix='supplierProfiles'
              viewLabel={t('labels.supplierProfilesView')}
              searchValue={searchStringInputValue}
              setSearchValue={setSearchStringInputValue}
              searchCleared={searchCleared}
              setSearchCleared={setSearchCleared}
              resetSearchFilter={resetSearchFilter}
              showPagination={showPagination}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              paginationDisabled={suppliersLoading === 'loading'}
              cardView={cardView}
              setCardView={setCardView}
              addButton={
                showPagination
                  ? {
                      id: 'supplierProfilesAddProfileBtn',
                      label: t('buttons.addProfile'),
                      onClick: handleCreateSupplier,
                    }
                  : undefined
              }
            />
            <Box className={listStyles.block_content}>
              {suppliersLoading === 'loading' && <ProgressOverlay position='absolute' />}
              {cardView ? (
                <CategoryCardGrid id='supplierProfilesGrid'>
                  {[...suppliersCreatedByAdmin]
                    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                    .map((supplier: SupplierWithId) => (
                      <CategoryCard
                        id={`supplierProfilesSuppliersCreatedByAdminCard_${supplier.id}`}
                        key={supplier.id}
                        title={supplier.name ?? ''}
                        icon={<BusinessCenterIcon />}
                        accent={CATEGORY_CARD_ACCENTS.Suppliers}
                        onClick={() => navigate(ROUTES.SUPPLIER_PROFILES + '/' + supplier.id)}
                        actions={
                          <Box className={styles.supplier_profile_icons}>
                            <Tooltip title={t('buttons.documents')} placement='top' arrow>
                              <IconButton
                                id={`supplierProfilesDocumentsIconBtn_${supplier.id}`}
                                aria-label={t('buttons.documents')}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDocuments(supplier.id);
                                }}
                                size='small'
                              >
                                <UploadFileOutlinedIcon
                                  id={`supplierProfilesDocumentsIconBtnUploadFileIcon_${supplier.id}`}
                                  fontSize='small'
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('buttons.edit')} placement='top' arrow>
                              <IconButton
                                id={`supplierProfilesEditIconBtn_${supplier.id}`}
                                aria-label={t('buttons.edit')}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleEdit(supplier.id);
                                }}
                                size='small'
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon
                                  id={`supplierProfilesEditIconBtnEditIcon_${supplier.id}`}
                                  fontSize='small'
                                />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              id={`supplierProfilesMoreOptionsIconBtn_${supplier.id}`}
                              aria-label={t('labels.menuButton')}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleClick(event, supplier.id);
                              }}
                              size='small'
                              sx={{ p: 0.5 }}
                            >
                              <MoreVertIcon
                                id={`supplierProfilesMoreOptionsIconBtnMoreVertIcon_${supplier.id}`}
                                fontSize='small'
                              />
                            </IconButton>
                          </Box>
                        }
                      />
                    ))}
                  <Button
                    id='supplierProfilesAddProfileBtn2'
                    onClick={handleCreateSupplier}
                    variant='contained'
                    color='primary'
                    className={styles.add_button}
                  >
                    <Box
                      id='supplierProfilesAddProfileBtn2Box'
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: '1rem',
                      }}
                    >
                      <Typography
                        id='supplierProfilesAddProfileBtn2Text'
                        sx={{ fontWeight: 'light', fontSize: 14 }}
                      >
                        {t('buttons.addProfile')}
                      </Typography>
                      <AddIcon id='supplierProfilesAddProfileBtn2AddIcon' />
                    </Box>
                  </Button>
                </CategoryCardGrid>
              ) : (
                <SuppliersTable isEmpty={suppliersCreatedByAdmin.length === 0}>
                  {[...suppliersCreatedByAdmin].map((supplier) => (
                    <SupplierRow
                      supplier={supplier}
                      key={supplier.id}
                      handleDelete={() => handleDelete(supplier.id)}
                      handleEdit={() => handleEdit(supplier.id)}
                      navigateToDocs={() => handleDocuments(supplier.id)}
                    />
                  ))}
                </SuppliersTable>
              )}
            </Box>
          </Box>
        </Grid>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMoreVertClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem
            id='supplierProfilesDeleteMenuItem'
            onClick={() => setDeleteMessageModalOpen(true)}
          >
            {t('buttons.delete')}
          </MenuItem>
        </Menu>
        {planMessageVisible && (
          <MessageModal
            open={planMessageVisible}
            modalMessage={t('messages.limitSuppliers')}
            actionButtonText={t('buttons.ok')}
            actionButtonFn={() => navigate(ROUTES.PRICING)}
            goBackButtonText={t('buttons.later')}
            onClose={() => {
              setPlanMessageVisible(false);
            }}
          />
        )}
        {deleteMessageModalOpen && (
          <MessageModal
            open={deleteMessageModalOpen}
            modalTitle={t('messages.deleteSupplier')}
            modalMessage={t('messages.deleteSupplierDialog')}
            actionButtonText={t('buttons.goBack')}
            actionButtonFn={() => setDeleteMessageModalOpen(false)}
            goBackButtonText={t('buttons.delete')}
            onDismiss={() => setDeleteMessageModalOpen(false)}
            onClose={() => {
              handleProfileDelete();
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default SupplierProfiles;
